import {
  useCallback,
  useLayoutEffect,
  type MutableRefObject,
  type RefObject
} from 'react';

type ElementRef =
  | RefObject<HTMLElement | null>
  | MutableRefObject<HTMLElement | null>;

export interface UseAnchorRelativeOverlayPositionOptions {
  anchorRef: ElementRef;
  /** Positioning context (`position: relative`) — rects are subtracted from this */
  overlayRootRef: ElementRef;
  /** The absolutely positioned node whose `left` / `top` / `visibility` are set */
  overlayRef: ElementRef;
  /** When this changes, position is flushed synchronously and on the next frame */
  layoutKey?: unknown;
  /** Minimum inset from `overlayRoot` edges when clamping horizontally */
  margin?: number;
  /** Vertical gap between anchor bottom and overlay top (overlay coordinates) */
  gapY?: number;
  /** If false, no position updates or scroll/resize listeners (e.g. different positioning mode) */
  enabled?: boolean;
}

/**
 * Positions `overlayRef` under `anchorRef` inside `overlayRootRef`, clamps
 * horizontally to the root width, and repositions on scroll/resize.
 *
 * Scroll is observed with capture on `window` and `document` so repositioning
 * runs when any in-document scrollable region scrolls (including nodes that were
 * remounted after route/panel updates), without caching `querySelector` results.
 */
export function useAnchorRelativeOverlayPosition({
  anchorRef,
  overlayRootRef,
  overlayRef,
  layoutKey,
  margin = 8,
  gapY = 4,
  enabled = true
}: UseAnchorRelativeOverlayPositionOptions): void {
  const flushPosition = useCallback(() => {
    if (!enabled) return;
    const anchor = anchorRef.current;
    const root = overlayRef.current;
    const overlay = overlayRootRef.current;
    if (!anchor || !root || !overlay) return;

    const ar = anchor.getBoundingClientRect();
    const or = overlay.getBoundingClientRect();

    let left = ar.left - or.left;
    const menuWidth = root.offsetWidth || root.getBoundingClientRect().width;
    if (menuWidth > 0) {
      left = Math.max(
        margin,
        Math.min(left, overlay.clientWidth - menuWidth - margin)
      );
    } else {
      left = Math.max(margin, left);
    }

    const top = ar.bottom - or.top + gapY;

    root.style.position = 'absolute';
    root.style.left = `${left}px`;
    root.style.top = `${top}px`;
    root.style.visibility = 'visible';
  }, [anchorRef, overlayRootRef, overlayRef, margin, gapY, enabled]);

  useLayoutEffect(() => {
    if (!enabled) return;
    flushPosition();
    const id = requestAnimationFrame(flushPosition);
    return () => cancelAnimationFrame(id);
  }, [enabled, flushPosition, layoutKey]);

  useLayoutEffect(() => {
    if (!enabled) return;
    const scrollOpts: AddEventListenerOptions = {
      capture: true,
      passive: true
    };
    const onScrollOrResize = () => flushPosition();

    window.addEventListener('scroll', onScrollOrResize, scrollOpts);
    document.addEventListener('scroll', onScrollOrResize, scrollOpts);
    window.addEventListener('resize', onScrollOrResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScrollOrResize, scrollOpts);
      document.removeEventListener('scroll', onScrollOrResize, scrollOpts);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [enabled, flushPosition]);
}
